import { axiosInstance } from "../axios.config";
import type { BidResponseDto } from "./bids.service";

/** PRD Epic 7 / FR-54: % (up to the admin cap) or a flat amount held to the cap */
export type ReferralFeeType = "percent" | "flat";

export interface OfferFeeTerms {
  referralFeeApplies: boolean;
  type: ReferralFeeType | null;
  value: number | null;
  capPercent: number | null;
}

export interface SellerInviteOption {
  inviteId: string;
  agentId: string;
  agentName: string;
  acceptedAt: string | null;
}

export interface Stalemate {
  gap: number;
  currentFee: number;
  closable: boolean;
  reduceBy: number | null;
  prompt: string | null;
}

export interface AgentNegotiation {
  bidId: string;
  listingId: string;
  listingAddress: string;
  status: string;
  buyerGross: number;
  referralFee: number;
  netToSeller: number;
  originalFee: number;
  sellerAskNet: number | null;
  stalemate: Stalemate | null;
  updatedAt: string;
}

export interface OutcomeCounts {
  won: number;
  lost: number;
  open: number;
}

export interface CoachingReport {
  reductions: number;
  totalReduced: number;
  negotiations: { reduced: OutcomeCounts; notReduced: OutcomeCounts };
  deals: OutcomeCounts;
  recentReductions: {
    listingId: string;
    listingAddress: string;
    oldFee: number;
    newFee: number;
    gap: number | null;
    at: string;
    outcome: keyof OutcomeCounts;
  }[];
}

export interface BuyNowTerms {
  typeOfFinancing: string;
  appraisalContingency: string;
  titleAndEscrowFees: string;
  daysToCloseEscrow: number;
  possessionAtCloseOfEscrow: string;
  termiteInspection: string;
  dueDiligenceDays: number | null;
}

export type BuyNowMinStatus = "proven" | "trusted";

/** PRD 6.5: a listing's Buy Now. Buyers also get gross / fee / net (6.2) */
export interface BuyNowInfo {
  /** The seller's price (net to seller) */
  price: number;
  terms: BuyNowTerms;
  minStatus: BuyNowMinStatus;
  eligible: boolean;
  reason: string | null;
  grossAmount?: number;
  referralFee?: number;
  netToSeller?: number;
  referralFeeApplies?: boolean;
}

export const referralFeesService = {
  getSettings: async (): Promise<{ capPercent: number }> =>
    (await axiosInstance.get("/referral-fees/settings")).data,

  getSellerInvites: async (): Promise<SellerInviteOption[]> =>
    (await axiosInstance.get("/referral-fees/seller-invites")).data,

  getNegotiations: async (): Promise<AgentNegotiation[]> =>
    (await axiosInstance.get("/referral-fees/negotiations")).data,

  reduce: async (bidId: string, newFee: number): Promise<AgentNegotiation> =>
    (await axiosInstance.post(`/referral-fees/negotiations/${bidId}/reduce`, { newFee })).data,

  getCoachingReport: async (): Promise<CoachingReport> =>
    (await axiosInstance.get("/referral-fees/coaching-report")).data,

  getOfferFee: async (listingId: string): Promise<OfferFeeTerms> =>
    (await axiosInstance.get(`/listings/${listingId}/referral-fee`)).data,

  getBuyNow: async (listingId: string): Promise<BuyNowInfo | null> =>
    (await axiosInstance.get(`/listings/${listingId}/buy-now`)).data || null,

  setBuyNow: async (
    listingId: string,
    body: { price: number | null; terms?: BuyNowTerms; minStatus?: BuyNowMinStatus },
  ): Promise<unknown> => (await axiosInstance.put(`/listings/${listingId}/buy-now`, body)).data,

  executeBuyNow: async (listingId: string, legalEntityId?: string): Promise<BidResponseDto> =>
    (await axiosInstance.post(`/listings/${listingId}/buy-now`, { legalEntityId })).data,
};
